/**
 * useAcpAccess — the frontend end of THE ONE ACP access function
 * (clarab/services/acpAccessService.js, GET /api/auth/acp-access): may this
 * login use the /acp page? The verdict is "the email you log in with has a
 * stake.com ACP account carrying a support-team role" — admins/developers
 * always. Shared by the sidebar (show the ACP entry?) and the page itself
 * (render the workbench, or explain why not, with a "Check again" button).
 *
 * One answer per session is cached at module level so the two callers share
 * it; a refresh from either broadcasts the new verdict to the other.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL;
export const ACP_ACCESS_EVENT = 'clara:acp-access-changed';
// The server is the authority; this list (its ACP_ACCESS_BYPASS_ROLES twin) only shapes the
// fallback when the lookup itself fails. Open to every role since 2026-09-23 — the 2026-09-22
// admin + ai-tester rollout list is gone.
const BYPASS_ROLES = ['admin', 'developer'];

let cache = { email: null, view: null };

export default function useAcpAccess() {
  const { user } = useAuth();
  const email = user?.email || null;
  const role = user?.role || null;
  const [view, setView] = useState(() => (email && cache.email === email ? cache.view : null));
  const [loading, setLoading] = useState(() => Boolean(email) && !(cache.email === email && cache.view));
  const reqRef = useRef(0);

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (!email) { setView(null); setLoading(false); return null; }
    const reqId = ++reqRef.current;
    setLoading(true);
    try {
      // No AbortController here on purpose: the answer is shared between hook
      // instances, and StrictMode's double effect would cancel the one the
      // other instance is waiting on. Stale responses are dropped by reqId.
      const { data } = await axios.get(
        `${API_URL}/api/auth/acp-access${refresh ? '?refresh=1' : ''}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (reqId !== reqRef.current) return data;
      cache = { email, view: data };
      setView(data);
      if (refresh) {
        try { window.dispatchEvent(new CustomEvent(ACP_ACCESS_EVENT, { detail: data })); } catch { /* non-browser */ }
      }
      return data;
    } catch (err) {
      if (reqId !== reqRef.current) return null;
      const failed = {
        granted: BYPASS_ROLES.includes(role),
        reason: 'lookup-failed',
        email,
        roles: [],
        matchedRoles: [],
        checkedAt: null,
        error: err.response?.data?.message || err.response?.data?.error || err.message || 'request failed',
      };
      setView(failed);
      return failed;
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [email, role]);

  useEffect(() => {
    if (!email) { setView(null); setLoading(false); return; }
    if (cache.email === email && cache.view) { setView(cache.view); setLoading(false); return; }
    load();
  }, [email, load]);

  // The page's "Check again" landed in another instance → adopt its verdict.
  useEffect(() => {
    const onChange = (e) => {
      if (!e?.detail) return;
      cache = { email, view: e.detail };
      setView(e.detail);
    };
    window.addEventListener(ACP_ACCESS_EVENT, onChange);
    return () => window.removeEventListener(ACP_ACCESS_EVENT, onChange);
  }, [email]);

  const granted = BYPASS_ROLES.includes(role) || view?.granted === true;
  const refresh = useCallback(() => load({ refresh: true }), [load]);
  return { loading, view, granted, refresh };
}
